import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, Check } from "lucide-react";
import { provincesSimplified } from "@/constants/provinces";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { usePaginationState, updatePaginationState } from "@/store/paginationStore";

/**
 * Configuration interface for the generic MultiProvinceFilter component
 */
interface MultiProvinceConfig<QueryVariables> {
  /** The pagination state key (e.g., "paginatedChristmasBonus", "paginatedHolidays") */
  paginationStateKey: keyof PaginationState;
  /** Function to build query variables with current filters and provinces */
  buildQueryVariables: (
    _displayAmount: number,
    _filters: any,
    _provinces: string[] | null
  ) => QueryVariables;
  /** Function to extract filters from pagination state */
  extractFilters: (_paginationState: any) => any;
}

interface Props<QueryVariables> {
  displayAmount: number;
  handleSubmitClick: (_variables: QueryVariables) => void;
  config: MultiProvinceConfig<QueryVariables>;
}

/**
 * Generic MultiProvinceFilter component
 * A reusable multi-select province dropdown for filtering data by provinces.
 * Can be configured for different configuration types (christmas bonus, holidays, etc.)
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - The number of items to display per page
 * @param {Function} props.handleSubmitClick - Callback invoked with updated query variables when province selection changes
 * @param {MultiProvinceConfig} props.config - Configuration object defining the behavior for this specific use case
 * @returns {JSX.Element} A multi-select province dropdown component
 *
 * @example
 * // For Christmas Bonus
 * <MultiProvinceFilter
 *   displayAmount={20}
 *   handleSubmitClick={handleChristmasBonusProvinceChange}
 *   config={christmasBonusProvinceConfig}
 * />
 */
const MultiProvinceFilter = <QueryVariables,>(props: Props<QueryVariables>) => {
  // Props
  const { displayAmount, handleSubmitClick, config } = props;

  // Hooks
  const { t } = useTranslation(["components", "common"]);

  // Pagination
  const paginationState = usePaginationState(config.paginationStateKey);
  const filters = config.extractFilters(paginationState);
  const provinces = filters?.provinces;

  // Track the selected provinces, default to all provinces
  const [selectedProvinces, setSelectedProvinces] = useState<string[]>(
    provinces || provincesSimplified.map(p => p.code)
  );

  // Handle province selection toggle
  const handleProvinceToggle = (provinceCode: string) => {
    const newSelection = selectedProvinces.includes(provinceCode)
      ? selectedProvinces.filter(code => code !== provinceCode)
      : [...selectedProvinces, provinceCode];

    setSelectedProvinces(newSelection);

    // Submit the updated selection with complete current filter state
    const provincesValue = newSelection.length === provincesSimplified.length ? null : newSelection;

    // Update pagination state with new provinces
    updatePaginationState({
      [config.paginationStateKey]: {
        filters: {
          ...filters,
          provinces: provincesValue
        }
      }
    });

    const updatedVariables = config.buildQueryVariables(displayAmount, filters, provincesValue);
    handleSubmitClick(updatedVariables);
  };

  // Handle select all provinces
  const handleSelectAll = () => {
    const allProvinces = provincesSimplified.map(p => p.code);
    setSelectedProvinces(allProvinces);

    // Update pagination state with all provinces (null means all selected)
    updatePaginationState({
      [config.paginationStateKey]: {
        filters: {
          ...filters,
          provinces: null
        }
      }
    });

    // Submit with complete current filter state
    const updatedVariables = config.buildQueryVariables(displayAmount, filters, null);
    handleSubmitClick(updatedVariables);
  };

  // Handle select none
  const handleSelectNone = () => {
    setSelectedProvinces([]);

    // Update pagination state with no provinces selected
    updatePaginationState({
      [config.paginationStateKey]: {
        filters: {
          ...filters,
          provinces: []
        }
      }
    });

    // Submit with complete current filter state
    const updatedVariables = config.buildQueryVariables(displayAmount, filters, []);
    handleSubmitClick(updatedVariables);
  };

  // Sync local state with global pagination state
  useEffect(() => {
    if (provinces === null) {
      // null means all provinces are selected
      setSelectedProvinces(provincesSimplified.map(p => p.code));
    } else if (Array.isArray(provinces)) {
      setSelectedProvinces(provinces);
    } else {
      // Single province or undefined
      setSelectedProvinces(provinces ? [provinces] : []);
    }
  }, [provinces]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="justify-between min-w-[200px]">
          {selectedProvinces.length === provincesSimplified.length
            ? t("components:filters.provinces.allprovinces")
            : selectedProvinces.length === 0
              ? t("components:filters.provinces.noprovinces")
              : `${selectedProvinces.length} ${t("components:filters.provinces.provincesselected")}`}
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-84 p-0">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-sm">
              {t("components:filters.provinces.selectprovinces")}
            </h4>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {t("components:filters.provinces.all")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectNone}
              >
                {t("components:filters.provinces.none")}
              </Button>
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {provincesSimplified.map((province) => (
              <div key={province.code} className="flex items-center space-x-2">
                <Checkbox
                  id={province.code}
                  checked={selectedProvinces.includes(province.code)}
                  onCheckedChange={() => handleProvinceToggle(province.code)}
                />
                <Label
                  htmlFor={province.code}
                  className="text-sm font-normal cursor-pointer flex-1"
                >
                  {province.name}
                </Label>
                {selectedProvinces.includes(province.code) && (
                  <Check className="h-4 w-4 text-slate-800" />
                )}
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MultiProvinceFilter;
